import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as faSolidStar } from '@fortawesome/free-solid-svg-icons';
import { faStar as faRegularStar } from '@fortawesome/free-regular-svg-icons';
import { Tooltip } from '@heroui/react';
import { Button } from './Button';
import { FavoriteTarget, UserFavorite } from 'src/api/types/favorite';
import { useFavorites } from 'src/contexts/FavoritesContext';

interface FavoriteButtonProps {
  target?: FavoriteTarget;
  label?: string;
  path?: string;
  targetMetadata?: UserFavorite['target'];
  className?: string;
  tooltipPlacement?: 'top' | 'bottom' | 'left' | 'right';
}

const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  target,
  label,
  path,
  targetMetadata,
  className,
  tooltipPlacement = 'top'
}) => {
  const { getFavorite, addFavorite, removeFavorite } = useFavorites();
  const [isPending, setIsPending] = useState(false);
  const favorite = target ? getFavorite(target) : undefined;
  const isFavorited = Boolean(favorite);

  if (!target) {
    return null;
  }

  const buttonClassName = [
    'h-6 w-6 min-w-6 text-default-400 hover:bg-default-100 hover:text-default-600 dark:text-white/45 dark:hover:bg-white/10 dark:hover:text-white/80',
    isFavorited ? 'text-warning-500 dark:text-warning-300' : '',
    className ?? ''
  ]
    .filter(Boolean)
    .join(' ');

  const handlePress = async () => {
    if (isPending) return;
    setIsPending(true);
    try {
      if (favorite) {
        await removeFavorite(favorite.id);
      } else {
        await addFavorite({
          ...target,
          label,
          path,
          target: targetMetadata
        });
      }
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Tooltip delay={400}>
      <Tooltip.Trigger>
        <Button
          isIconOnly
          size="sm"
          variant="ghost"
          className={buttonClassName}
          isDisabled={isPending}
          aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          onPress={handlePress}>
          <FontAwesomeIcon icon={isFavorited ? faSolidStar : faRegularStar} className="h-3 w-3" />
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content placement={tooltipPlacement} showArrow>
        <Tooltip.Arrow />
        {isFavorited ? 'Remove from favorites' : 'Add to favorites'}
      </Tooltip.Content>
    </Tooltip>
  );
};

export default FavoriteButton;
